import { useMemo } from 'react';
import type { Asset } from '@/types/GraphqlSchema';
import { formatDateShort } from '@/utils/helpers';
import type { Metric } from '../types';

const getOilCondensateReservesLabel = (asset: Asset | null): string => {
  return asset?.country?.haveLiquidsReserves ? 'Oil/Condensate Reserves' : 'Oil Reserves';
};

const getOilCondensateReserves = (asset: Asset | null): string | null => {
  if (!asset?.reserve) {
    return null;
  }

  if (asset.country?.haveLiquidsReserves) {
    return asset.reserve.formattedTotalRemainingReservesLiquidsMmbbl ?? null;
  }

  return asset.reserve.formattedTotalRemainingReservesOilMmbbl ?? null;
};

const getWellEursLabel = (asset: Asset | null): string => {
  return asset?.hydrocarbonType === 'oil' ? 'Oil Well EURs' : 'Gas Well EURs';
};

const getWellEursUnit = (asset: Asset | null): string => {
  return asset?.hydrocarbonType === 'oil' ? '(MMbbl)' : '(Bcf)';
};

const getWellEurs = (asset: Asset | null): string | null => {
  if (asset?.hydrocarbonType === 'oil') {
    return asset.formattedLiquidsEurMmbbl ?? null;
  }
  return asset?.formattedGasEurBcf ?? null;
};

const getWellRatesLabel = (asset: Asset | null): string => {
  return asset?.hydrocarbonType === 'oil' ? 'Oil Well IP Rates' : 'Gas Well IP Rates';
};

const getWellRatesUnit = (asset: Asset | null): string => {
  return asset?.hydrocarbonType === 'oil' ? '(bbl/d)' : '(MMcf/d)';
};

const getWellRates = (asset: Asset | null): string | null => {
  if (asset?.hydrocarbonType === 'oil') {
    return asset.formattedLiquidsIpBbld ?? null;
  }
  return asset?.formattedGasIpMmcfd ?? null;
};

const getProductionNotes = (asset: Asset | null): string | null => {
  const hasLiquidsProduction = asset?.formattedRecentProdLiquidsBbld;
  const hasGasProduction = asset?.formattedRecentProdGasMmcfd;

  if (hasLiquidsProduction || hasGasProduction) {
    return `(*) The production shown is actual if reported within the last 6 months; otherwise we show our most recent estimate.`;
  }

  return null;
};

export const useProductionAndReservesMetrics = (
  asset: Asset | null,
): { metrics: Metric[]; notes: string | null } => {
  const metrics = useMemo(() => {
    if (!asset) {
      return [];
    }

    const estimatedLiquidsDate = formatDateShort(asset.estimatedCurrentDate);
    const estimatedGasDate = formatDateShort(asset.estimatedCurrentDate);

    return [
      {
        label: 'Oil/Condensate Production',
        unit: '(bbl/d)',
        value: asset.formattedEstimatedRecentProdLiqBbld
          ? `${asset.formattedEstimatedRecentProdLiqBbld} *\n(${estimatedLiquidsDate})`
          : null,
      },
      {
        label: 'Gas Production',
        unit: '(MMcf/d)',
        value: asset.formattedEstimatedRecentProdGasMmcfd
          ? `${asset.formattedEstimatedRecentProdGasMmcfd} *\n(${estimatedGasDate})`
          : null,
      },
      {
        label: 'Current Emissions Intensity',
        unit: '(kgCO2/boe)',
        value: asset.formattedCurYrTotalProdIntensityKgco2ePerBoe ?? null,
      },
      {
        label: getOilCondensateReservesLabel(asset),
        unit: '(MMbbl)',
        value: getOilCondensateReserves(asset),
      },
      {
        label: 'Gas Reserves',
        unit: '(Bcf)',
        value: asset.reserve?.formattedTotalRemainingReservesGasBcf ?? null,
      },
      {
        label: getWellEursLabel(asset),
        unit: getWellEursUnit(asset),
        value: getWellEurs(asset),
      },
      {
        label: getWellRatesLabel(asset),
        unit: getWellRatesUnit(asset),
        value: getWellRates(asset),
      },
    ];
  }, [asset]);

  const notes = useMemo(() => getProductionNotes(asset), [asset]);

  return { metrics, notes };
};
